import { HEX } from '@somnhold/content';
import { idiv } from './math/fixed.ts';

/** Axial hex coordinates, pointy-top. */
export interface Hex {
  q: number;
  r: number;
}

/** Neighbor directions in a fixed order (deterministic iteration). */
export const DIRS: readonly Hex[] = [
  { q: 1, r: 0 },
  { q: 1, r: -1 },
  { q: 0, r: -1 },
  { q: -1, r: 0 },
  { q: -1, r: 1 },
  { q: 0, r: 1 },
];

export const hexDist = (a: Hex, b: Hex): number => {
  const dq = a.q - b.q;
  const dr = a.r - b.r;
  return idiv(Math.abs(dq) + Math.abs(dr) + Math.abs(dq + dr), 2);
};

export const hexLen = (q: number, r: number): number => idiv(Math.abs(q) + Math.abs(r) + Math.abs(q + r), 2);

export const hexKey = (h: Hex): string => `${h.q},${h.r}`;
export const parseHexKey = (k: string): Hex => {
  const [q, r] = k.split(',');
  return { q: Number(q), r: Number(r) };
};

/** Row height of a pointy-top hex grid whose neighbor centers are 1000 apart: 1000·√3/2. */
export const ROW = 866;

/** Center of a hex in fixed world units. x right, y "down" the map (render maps y → z). */
export const hexToWorld = (q: number, r: number): { x: number; y: number } => ({
  x: q * HEX + idiv(r * HEX, 2),
  y: r * ROW,
});

/** Nearest hex to a world point (integer cube rounding). */
export const worldToHex = (x: number, y: number): Hex => {
  // Fractional axial coordinates in thousandths.
  const rf = idiv(y * 1000, ROW);
  const qf = x - idiv(rf, 2);
  const sf = -qf - rf;
  let q = roundK(qf);
  let r = roundK(rf);
  const s = roundK(sf);
  const dq = Math.abs(q * 1000 - qf);
  const dr = Math.abs(r * 1000 - rf);
  const ds = Math.abs(s * 1000 - sf);
  if (dq > dr && dq > ds) q = -r - s;
  else if (dr > ds) r = -q - s;
  return { q, r };
};

/**
 * Cell id (see grid.ts) of the hex nearest a world point, or −1 outside radius R. Same rounding as
 * `worldToHex`, without building a Hex: it runs for every entity on every tick.
 */
export const worldToCell = (R: number, x: number, y: number): number => {
  const rf = idiv(y * 1000, ROW);
  const qf = x - idiv(rf, 2);
  const sf = -qf - rf;
  let q = roundK(qf);
  let r = roundK(rf);
  const s = roundK(sf);
  const dq = Math.abs(q * 1000 - qf);
  const dr = Math.abs(r * 1000 - rf);
  const ds = Math.abs(s * 1000 - sf);
  if (dq > dr && dq > ds) q = -r - s;
  else if (dr > ds) r = -q - s;
  if (hexLen(q, r) > R) return -1;
  return (r + R) * (2 * R + 1) + (q + R);
};

const roundK = (v: number): number => {
  // round(v / 1000) half away from zero, integers only
  const sign = v < 0 ? -1 : 1;
  return sign * idiv(Math.abs(v) + 500, 1000);
};

/** All hexes at exactly `radius` from center, in deterministic order. */
export const hexRing = (radius: number): Hex[] => {
  if (radius === 0) return [{ q: 0, r: 0 }];
  const out: Hex[] = [];
  let q = DIRS[4]!.q * radius;
  let r = DIRS[4]!.r * radius;
  for (let side = 0; side < 6; side++) {
    for (let i = 0; i < radius; i++) {
      out.push({ q, r });
      q += DIRS[side]!.q;
      r += DIRS[side]!.r;
    }
  }
  return out;
};

/** Hexes in a straight line between two hexes (inclusive), via fixed-point lerp. */
export const hexLine = (a: Hex, b: Hex): Hex[] => {
  const n = hexDist(a, b);
  const out: Hex[] = [];
  const A = hexToWorld(a.q, a.r);
  const B = hexToWorld(b.q, b.r);
  for (let i = 0; i <= n; i++) {
    const x = n === 0 ? A.x : A.x + idiv((B.x - A.x) * i, n);
    const y = n === 0 ? A.y : A.y + idiv((B.y - A.y) * i, n);
    out.push(worldToHex(x + 1, y + 1));
  }
  return out;
};
